import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import styles from './PortfolioDetail.module.scss';
import { CONSULTANT, DETAIL_FAQ, PROCESS_STEPS } from './portfolioItems';
import { fetchPortfolioItems, formatPortfolioPrice, getAdjacentItems, getRelatedItems, registerPortfolioView } from './portfolioService';
import { PortfolioItem } from './portfolio.types';
import { apiUrl } from '../../services/apiClient';

export interface PortfolioApplyPayload {
  shape: string;
  content: string;
}

const TABS = ['상세정보', '제작스펙', '시안 노트'];

function PortfolioDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [items, setItems] = useState<PortfolioItem[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const item = items?.find((candidate) => candidate.id === id);

  const [activeThumb, setActiveThumb] = useState(0);
  const [activeTab, setActiveTab] = useState(0);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    fetchPortfolioItems()
      .then(setItems)
      .catch((err) => setLoadError(err instanceof Error ? err.message : '포트폴리오를 불러오지 못했습니다.'));
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
    setActiveThumb(0);
    setActiveTab(0);
    setSaved(false);
  }, [id]);

  useEffect(() => {
    if (id) {
      registerPortfolioView(id);
    }
  }, [id]);

  if (!items && !loadError) {
    return <div className={styles.page} />;
  }

  if (!item) {
    return (
      <div className={styles.page}>
        <div className={styles.listBack}>
          <Link to="/portfolio" className={styles.btnList}>
            <svg viewBox="0 0 24 24">
              <path d="m15 18-6-6 6-6" />
            </svg>
            목록으로 돌아가기
          </Link>
        </div>
      </div>
    );
  }

  const related = getRelatedItems(items ?? [], item);
  const { prev, next } = getAdjacentItems(items ?? [], item);
  const reviewTab = `제작후기 (${item.detail.reviews.length})`;
  const allTabs = [...TABS, reviewTab];

  const handleShare = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
    } catch {
      // 클립보드 접근이 불가한 환경에서는 조용히 무시
    }
  };

  return (
    <div className={styles.page}>
      {/* TOP META */}
      <div className={styles.topMeta}>
        <div className={styles.crumb}>
          <Link to="/">HOME</Link>
          <svg viewBox="0 0 24 24">
            <path d="m9 18 6-6-6-6" />
          </svg>
          <Link to="/order">주문하기</Link>
          <svg viewBox="0 0 24 24">
            <path d="m9 18 6-6-6-6" />
          </svg>
          <Link to="/portfolio">디자인 없음</Link>
          <svg viewBox="0 0 24 24">
            <path d="m9 18 6-6-6-6" />
          </svg>
          <span className={styles.crumbHere}>{item.code}</span>
          <span className={styles.crumbCode}>
            {item.brand} × G-WORKS
          </span>
        </div>
      </div>

      {/* DETAIL HERO */}
      <section className={styles.detailHero}>
        <div className={styles.gallery}>
          {item.thumbnailUrl ? (
            <div className={styles.gMain}>
              <img src={apiUrl(item.thumbnailUrl)} alt={item.name} className={styles.gMainImg} />
            </div>
          ) : (
            <>
              <div className={`${styles.gMain} ${styles[`thumb${activeThumb + 1}`]}`}>
                <span className={styles.gMainDeco}>{item.detail.galleryDecos[activeThumb]}</span>
                <button className={styles.gZoom} aria-label="확대">
                  <svg viewBox="0 0 24 24">
                    <circle cx="11" cy="11" r="7" />
                    <path d="m21 21-4.3-4.3" />
                    <line x1="11" y1="8" x2="11" y2="14" />
                    <line x1="8" y1="11" x2="14" y2="11" />
                  </svg>
                </button>
              </div>
              <div className={styles.gThumbs}>
                {item.detail.galleryDecos.map((deco, i) => (
                  <button
                    key={i}
                    type="button"
                    className={`${styles.gThumb} ${styles[`thumb${i + 1}`]} ${i === activeThumb ? styles.active : ''}`}
                    onClick={() => setActiveThumb(i)}
                  >
                    <div className={styles.gThumbDeco}>{deco}</div>
                  </button>
                ))}
              </div>
            </>
          )}
        </div>

        <div className={styles.info}>
          <div className={styles.infoTag}>{item.detail.infoTag}</div>
          <div>
            <div className={styles.infoBrand}>{item.brand}</div>
            <h1 className={styles.infoTitle}>{item.name}</h1>
            <p className={styles.infoSub}>
              {item.detail.subCopy.map((line, i) => (
                <span key={i}>
                  {line}
                  {i < item.detail.subCopy.length - 1 && <br />}
                </span>
              ))}
            </p>
          </div>

          <div className={styles.infoPrice}>
            <strong>{formatPortfolioPrice(item)}</strong>
            {item.price !== null && item.priceNote && <span>{item.priceNote}</span>}
          </div>

          <div className={styles.infoDivider} />

          <table className={styles.specTable}>
            <tbody>
              {item.detail.specs.map((spec) => (
                <tr key={spec.label}>
                  <th>{spec.label}</th>
                  <td>
                    {spec.pills?.map((pill) => (
                      <span key={pill} className={styles.pill}>
                        {pill}
                      </span>
                    ))}
                    {spec.value}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className={styles.infoActions}>
            {/* 가격이 등록된 상품만 바로 결제할 수 있다 */}
            {item.price !== null && (
              <button
                type="button"
                className={styles.btnPrimary}
                onClick={() => navigate(`/order/checkout/${item.id}`)}
              >
                <svg viewBox="0 0 24 24">
                  <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                </svg>
                이 상품으로 주문하기
              </button>
            )}
            <button type="button" className={styles.btnSecondary}>
              <svg viewBox="0 0 24 24">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <path d="m7 10 5 5 5-5" />
                <path d="M12 15V3" />
              </svg>
              시안 PDF
            </button>
            <button
              type="button"
              className={`${styles.saveBtn} ${saved ? styles.active : ''}`}
              aria-label="저장"
              aria-pressed={saved}
              onClick={() => setSaved((s) => !s)}
            >
              <svg viewBox="0 0 24 24">
                <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
              </svg>
            </button>
          </div>

          <div className={styles.infoNote}>
            <svg viewBox="0 0 24 24">
              <circle cx="12" cy="12" r="10" />
              <path d="M12 8v4" />
              <path d="M12 16h.01" />
            </svg>
            <span>
              결제 후 담당자가 1:1 시안을 올려드립니다. 시안 수정은 최대 3회까지 무료입니다.
            </span>
          </div>

          <div className={styles.shareRow}>
            <span>SHARE</span>
            <div className={styles.shareIcons}>
              <button type="button" aria-label="링크 복사" onClick={handleShare}>
                <svg viewBox="0 0 24 24">
                  <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
                  <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
                </svg>
              </button>
              <button type="button" aria-label="페이스북">
                <svg viewBox="0 0 24 24">
                  <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z" />
                </svg>
              </button>
              <button type="button" aria-label="카톡">
                <svg viewBox="0 0 24 24">
                  <path d="M12 3C6.5 3 2 6.5 2 11c0 2.8 1.9 5.3 4.7 6.7-.2.7-.6 2-.6 2s1-1 2-1.5c1.2.3 2.5.5 3.9.5 5.5 0 10-3.5 10-8s-4.5-8-10-8z" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* TABS */}
      <div className={styles.tabsStrip}>
        <div className={styles.tabs}>
          {allTabs.map((tab, i) => (
            <div
              key={tab}
              className={`${styles.tab} ${i === activeTab ? styles.active : ''}`}
              onClick={() => setActiveTab(i)}
            >
              {tab}
            </div>
          ))}
          <ul className={styles.tabMeta}>
            <li>조회 {item.viewCount.toLocaleString()}</li>
            <li>· 업데이트 {item.updatedAt}</li>
          </ul>
        </div>
      </div>

      {/* DETAIL BODY */}
      <section className={styles.detailBody}>
        <div>
          <div className={styles.galleryStack}>
            {item.detail.stackImages.map((stack, i) => (
              <div key={i}>
                <div className={`${styles.stackImg} ${styles[`tone${stack.tone}`]}`}>
                  <span className={styles.stackDeco}>{stack.deco}</span>
                </div>
                <div className={styles.stackCaption}>
                  <span className={styles.stackCaptionLeft}>{stack.captionLeft}</span>
                  <span>{stack.captionRight}</span>
                </div>
              </div>
            ))}
          </div>

          <div className={styles.processBlock}>
            <h3>{item.detail.processTitle}</h3>
            {item.detail.processLead.map((lead, i) => (
              <p key={i} className={styles.lead}>
                {lead}
              </p>
            ))}
          </div>

          <div className={styles.processBlock}>
            <h3>제작 프로세스</h3>
            <div className={styles.stepList}>
              {PROCESS_STEPS.map((step) => (
                <div key={step.num} className={styles.step}>
                  <div className={styles.stepNum}>{step.num}</div>
                  <div className={styles.stepTitle}>{step.title}</div>
                  <div className={styles.stepDesc}>{step.desc}</div>
                </div>
              ))}
            </div>
          </div>

          <div className={styles.processBlock}>
            <h3>고객 후기</h3>
            <div className={styles.qaList} style={{ marginTop: 24 }}>
              {item.detail.reviews.map((review) => (
                <div key={review.question} className={styles.qaItem}>
                  <div className={styles.qaQ}>{review.question}</div>
                  <div className={styles.qaA}>{review.answer}</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <aside className={styles.side}>
          <div className={styles.sideCard}>
            <div className={styles.sideTitle}>담당 컨설턴트</div>
            <p className={styles.consultantDesc}>
              본 프로젝트의 시안부터 납품까지
              <br />
              함께한 담당자를 연결해드립니다.
            </p>
            <div className={styles.consultantCard}>
              <div className={styles.consultantAvatar}>{CONSULTANT.initial}</div>
              <div>
                <div className={styles.consultantName}>{CONSULTANT.name}</div>
                <div className={styles.consultantContact}>
                  {CONSULTANT.email} · {CONSULTANT.phone}
                </div>
              </div>
            </div>
          </div>

          <div className={styles.sideCard}>
            <div className={styles.sideTitle}>시안에서 자주 묻는 질문</div>
            <div className={styles.qaList}>
              {DETAIL_FAQ.map((faq) => (
                <div key={faq.question} className={styles.qaItem}>
                  <div className={styles.qaQ}>{faq.question}</div>
                  <div className={styles.qaA}>{faq.answer}</div>
                </div>
              ))}
            </div>
          </div>
        </aside>
      </section>

      {/* RELATED */}
      {related.length > 0 && (
        <section className={styles.related}>
          <div className={styles.relHead}>
            <div>
              <h2 className={styles.relTitle}>비슷한 형태의 다른 작업</h2>
            </div>
            <Link to="/portfolio" className={styles.relMore}>
              VIEW ALL →
            </Link>
          </div>
          <div className={styles.relGrid}>
            {related.map((rel) => (
              <Link key={rel.id} to={`/portfolio/${rel.id}`} className={styles.relCard}>
                <div className={`${styles.relThumb} ${styles[`tone${rel.tone}`]}`}>
                  <span className={styles.relDeco}>{rel.deco}</span>
                  <span className={styles.relCode}>{rel.code}</span>
                </div>
                <div className={styles.relMeta}>
                  <span className={styles.relBrand}>{rel.brand}</span>
                  <h4 className={styles.relName}>{rel.name}</h4>
                  <span className={styles.relTag}>{rel.tag}</span>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* WORK NAV */}
      <div className={styles.workNav}>
        <Link className={`${styles.wnCell} ${styles.prev}`} to={`/portfolio/${prev.id}`}>
          <span className={styles.arrowBox}>
            <svg viewBox="0 0 24 24">
              <path d="m15 18-6-6 6-6" />
            </svg>
          </span>
          <div>
            <div className={styles.wnTitle}>{prev.name}</div>
            <div className={styles.wnTag}>
              {prev.code} · {prev.brand}
            </div>
          </div>
        </Link>
        <Link className={`${styles.wnCell} ${styles.next}`} to={`/portfolio/${next.id}`}>
          <div>
            <div className={styles.wnTitle}>{next.name}</div>
            <div className={styles.wnTag}>
              {next.code} · {next.brand}
            </div>
          </div>
          <span className={styles.arrowBox}>
            <svg viewBox="0 0 24 24">
              <path d="m9 6 6 6-6 6" />
            </svg>
          </span>
        </Link>
      </div>

      <div className={styles.listBack}>
        <Link className={styles.btnList} to="/portfolio">
          <svg viewBox="0 0 24 24">
            <path d="m15 18-6-6 6-6" />
          </svg>
          목록으로 돌아가기
        </Link>
      </div>
    </div>
  );
}

export default PortfolioDetail;
